<script lang="ts">
  import { geoMercator, geoPath } from 'd3-geo';
  import type { ExtendedFeature, ExtendedFeatureCollection } from 'd3-geo';
  import { feature } from 'topojson-client';
  import type { GeometryCollection, Topology } from 'topojson-specification';
  import type { ChartGeospatial } from '$lib/sanity/schemas';
  import geoData from './assets/world-countries.json';
  import ChartLayout from './components/ChartLayout.svelte';
  import Legend from './components/Legend.svelte';
  import Table from './components/Table.svelte';
  import { setUpChart } from './utils/chart';
  import type { ColorPalette } from './utils/color';
  import { categorizeChartItems, formatDocumentId } from './utils/data';
  import { interactionStore } from './utils/interactions';
  import {
    useEncodeDataAttribute,
    type ContentSourceMap,
    type StudioPathLike
  } from '@sanity/svelte-loader';
  import { sanityStudioURL } from '$lib/client';

  interface Props {
    document: ChartGeospatial;
    sourceMap: ContentSourceMap;
    path?: StudioPathLike;
    gridColspan: number;
  }

  let { document, sourceMap, path = [], gridColspan }: Props = $props();

  let encodeDataAttribute = $derived(useEncodeDataAttribute(document, sourceMap, sanityStudioURL));

  enum colorThemes {
    MAP_FILL = 'theme-gradient-fill',
    MAP_BACKGROUND = 'theme-gradient-background'
  }

  type GeoProperties = {
    name: string;
    'Alpha-2': string;
  };

  const {
    _id,
    alt,
    chart = null,
    colorTheme = '',
    footnote = null,
    headline = '',
    subhead = ''
  } = document;
  const { items = [] } = chart || {};
  const categories = categorizeChartItems(items);
  const countries = items.map((item) => item.country);
  const tableProps = {
    header: categories.primary,
    rows: items.map((item) => [item.country])
  };
  const colorPalette: ColorPalette = {
    name: 'map gradient',
    colors: [
      {
        id: 'map-gradient',
        colors: ['#FF4F00', '#ECA3FC', '#2ED7C8'],
        angle: 90
      }
    ],
    isGradient: true
  };
  const useGradientFillTheme = !colorTheme.length || colorTheme === colorThemes.MAP_FILL;
  const id = formatDocumentId(_id);
  const store = interactionStore(id);
  const gradientId = colorPalette?.colors[0].id;
  // Use a random number to create a unique mask
  const maskId = `map-gradient-mask-${Math.random()}`;

  const renderChart = (chartElement: HTMLElement) => {
    const chart = setUpChart(chartElement, { alt, colorPalette });
    const { selection, sizes } = chart;
    const unkownFeatureData = geoData as unknown;
    const featureData: Topology = unkownFeatureData as Topology;
    const unkownCountryData = featureData.objects.countries1 as unknown;
    const countryData = unkownCountryData as GeometryCollection<GeoProperties>;
    const countriesSansAntarctica: GeometryCollection<GeoProperties> = {
      type: countryData?.type,
      geometries: countryData?.geometries.filter((item) => {
        const properties = item?.properties as GeoProperties;

        return properties.name !== 'Antarctica';
      })
    };

    const land = feature(featureData, countriesSansAntarctica) as ExtendedFeatureCollection<
      ExtendedFeature<any, GeoProperties>
    >;

    const selectedCountries = land.features.filter((feature) =>
      countries.includes(feature?.properties?.name)
    );
    const projection = geoMercator()
      .fitSize([sizes.graph.width, sizes.graph.height], land)
      // Rotate the map to prevent the clipping of Russia
      .rotate([-10, 0]);
    const mapProjection = geoPath().projection(projection);

    if (useGradientFillTheme) {
      // Draw a mask made up of the selected countries
      selection
        .append('defs')
        .append('mask')
        .attr('id', maskId)
        .attr('x', 0)
        .attr('y', 0)
        .attr('width', '100%')
        .attr('height', '100%')
        .selectAll('paths')
        .data(selectedCountries)
        .enter()
        .append('path')
        .attr('d', mapProjection)
        .attr('fill', `#ffffff`)
        .attr('stroke', '#000000')
        .attr('stroke-width', '1px');
    }

    // Draw glow behind the countries
    if (!useGradientFillTheme) {
      selection
        .selectAll('map-countries-glow')
        .data(selectedCountries)
        .enter()
        .append('path')
        .attr('d', mapProjection)
        .style('filter', 'drop-shadow(0 0 0.75rem #ffffff)');
    }

    // Draw countries
    selection
      .selectAll('map-countries')
      .data(land.features)
      .enter()
      .append('path')
      .attr('d', mapProjection)
      .attr('fill', function (d) {
        const selected = countries.includes(d?.properties?.name);

        if (useGradientFillTheme) {
          return selected ? `url(#${gradientId})` : '#ffffff';
        } else if (colorTheme === colorThemes.MAP_BACKGROUND) {
          return selected ? '#ffffff' : 'none';
        }

        return '#ffffff';
      })
      .attr('stroke', '#000000')
      .attr('stroke-width', '1px');

    // Draw the gradient and apply the mask
    if (useGradientFillTheme) {
      selection
        .append('rect')
        .attr('x', 0)
        .attr('y', 0)
        .attr('width', '100%')
        .attr('height', '100%')
        .attr('mask', `url(#${maskId})`)
        .attr('fill', `url(#${gradientId})`);
    }
  };
</script>

<ChartLayout
  {footnote}
  {headline}
  {id}
  {subhead}
  {renderChart}
  {path}
  {encodeDataAttribute}
  {gridColspan}
>
  {#snippet legend()}
    <Legend categories={categories.primary} {colorPalette} {store} {gridColspan} />
  {/snippet}
  {#snippet table()}
    <Table {...tableProps} {gridColspan} />
  {/snippet}
</ChartLayout>
